import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { apiFetch } from '@/lib/api';
import { isOssStorageMode, MEDIA_QUOTA_EVENT, type MediaStorageStatus } from '@/lib/mediaStorage';
import { MediaQuotaModal } from '@/components/MediaQuotaModal';
import { useSession } from '@/hooks/useSession';

type Ctx = {
  mediaStorage: MediaStorageStatus | null;
  assertUploadAllowed: () => boolean;
};

const MediaStorageContext = createContext<Ctx>({
  mediaStorage: null,
  assertUploadAllowed: () => true,
});

export function useMediaStorage() {
  return useContext(MediaStorageContext);
}

type MediaOssGetResp = { ok: boolean; media_storage?: MediaStorageStatus };

export function MediaStorageProvider({ children }: { children: ReactNode }) {
  const { ok, role, mediaStorage, refresh } = useSession();
  const [usageDetail, setUsageDetail] = useState<MediaStorageStatus | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalVariant, setModalVariant] = useState<'blocked' | 'warn'>('blocked');

  const effectiveMedia = usageDetail ?? mediaStorage;

  useEffect(() => {
    if (!ok || role === 'admin') {
      setUsageDetail(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const r = await apiFetch<MediaOssGetResp>('get_user_media_oss_config', { method: 'GET' });
        if (!cancelled && r.ok && r.media_storage) {
          setUsageDetail(r.media_storage);
        }
      } catch {
        /* 侧栏用量非关键路径 */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ok, role, mediaStorage?.storage_mode]);

  const showBlocked = useCallback((status?: MediaStorageStatus | null) => {
    setModalVariant('blocked');
    setModalOpen(true);
  }, []);

  useEffect(() => {
    const onBlocked = (ev: Event) => {
      const detail = (ev as CustomEvent<MediaStorageStatus>).detail;
      showBlocked(detail ?? effectiveMedia);
    };
    window.addEventListener(MEDIA_QUOTA_EVENT, onBlocked);
    return () => window.removeEventListener(MEDIA_QUOTA_EVENT, onBlocked);
  }, [effectiveMedia, showBlocked]);

  const assertUploadAllowed = useCallback(() => {
    if (!ok || role === 'admin') return true;
    if (!effectiveMedia || isOssStorageMode(effectiveMedia.storage_mode)) return true;
    if (effectiveMedia.upload_blocked) {
      showBlocked(effectiveMedia);
      return false;
    }
    return true;
  }, [ok, role, effectiveMedia, showBlocked]);

  const value = useMemo(
    () => ({ mediaStorage: ok ? effectiveMedia : null, assertUploadAllowed }),
    [ok, effectiveMedia, assertUploadAllowed]
  );

  return (
    <MediaStorageContext.Provider value={value}>
      {children}
      <MediaQuotaModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          void refresh();
        }}
        status={effectiveMedia}
        variant={modalVariant}
      />
    </MediaStorageContext.Provider>
  );
}
